// External-adviser audience helpers (Phase 3, F1).
//
// Two ways to learn that the current principal is an *external adviser*:
//   1. Read the `audience` custom claim minted into the access-token JWT by the
//      Supabase custom-access-token hook (S3–S6). This is the cheap, network-free path.
//   2. Fall back to a self-scoped `adviser_links` SELECT (RLS returns only the caller's
//      own row) when the claim is absent — which it is until the hook is registered.
//
// Both are DEFENSIVE by construction:
//   - `readAudienceClaim` never throws; a malformed / missing token yields `null`.
//   - `hasAdviserLink` swallows *every* error — including `42P01 undefined_table`
//     (the table ships in migration m30, unapplied at merge time) — and returns `false`.
//
// This is what makes the whole file safe to merge BEFORE any backend lands: with no
// hook and no table, `readAudienceClaim` returns `null` and `hasAdviserLink` returns
// `false`, so the adviser branch is inert. Staff behaviour is entirely unaffected — no
// staff code path calls into this module.

import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

/**
 * base64url-decode a single JWT segment to its UTF-8 string. Never throws;
 * returns `null` on any malformed input.
 */
function decodeJwtSegment(segment: string): string | null {
  try {
    // JWT uses base64url (`-`/`_`, no padding). Normalise to standard base64.
    let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4;
    if (pad === 2) base64 += '==';
    else if (pad === 3) base64 += '=';
    else if (pad === 1) return null; // invalid length

    // `atob` exists in the browser (and in the RN/Hermes runtime via a polyfill);
    // guard anyway so this never explodes in an unexpected environment.
    if (typeof atob !== 'function') return null;
    const binary = atob(base64);

    // Decode the binary string as UTF-8 so multi-byte claim values survive.
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    if (typeof TextDecoder === 'function') {
      return new TextDecoder('utf-8').decode(bytes);
    }
    return binary;
  } catch {
    return null;
  }
}

/**
 * Read the `audience` custom claim from a session's access token.
 *
 * Returns `'adviser'` iff the decoded JWT payload carries `audience === 'adviser'`
 * (checking both the top-level claim and `app_metadata.audience`, mirroring the
 * backend `audience_from_jwt()` COALESCE order). Returns `null` for anything else —
 * no session, no token, no claim, malformed token, or a non-adviser audience.
 *
 * Never throws.
 */
export function readAudienceClaim(session: Session | null | undefined): 'adviser' | null {
  const token = session?.access_token;
  if (!token || typeof token !== 'string') return null;

  const parts = token.split('.');
  if (parts.length !== 3) return null;

  const json = decodeJwtSegment(parts[1]);
  if (!json) return null;

  try {
    const payload = JSON.parse(json) as {
      audience?: unknown;
      app_metadata?: { audience?: unknown } | null;
    };
    const claim =
      (typeof payload?.audience === 'string' ? payload.audience : undefined) ??
      (typeof payload?.app_metadata?.audience === 'string'
        ? payload.app_metadata?.audience
        : undefined);
    return claim === 'adviser' ? 'adviser' : null;
  } catch {
    return null;
  }
}

/**
 * DB fallback: does the current authenticated user have an active `adviser_links`
 * row? RLS restricts the SELECT to the caller's own row, so this returns a single
 * row (or none) for the logged-in user.
 *
 * Swallows every error — including `42P01 undefined_table` before m30 is applied —
 * and returns `false`. This is what keeps the adviser branch inert (and this call
 * cheap and safe) until the backend identity floor exists.
 *
 * NOTE: `adviser_links` is not yet in the generated Supabase types (it lands in m30),
 * so we use the repo's established `.from('table' as any)` escape hatch.
 */
export async function hasAdviserLink(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('adviser_links' as any)
      .select('user_id')
      .eq('status', 'active')
      .maybeSingle();

    if (error) return false;
    return !!data;
  } catch {
    return false;
  }
}
